<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBradleyEnvironment } from '@/shared/composables/use-bradley-environment.js'
import {
  BRADLEY_MAX_FILES_PER_MESSAGE,
  bradleyDisclaimerTypesLabel,
  formatBradleyFileSize,
  getBradleyAttachmentPolicy,
} from '../utils/bradley-attachment.util.js'

const props = defineProps({
  error: {
    type: Object,
    default: null,
  },
  errors: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['dismiss'])

const { t } = useI18n()
const { newFileUploadEnabled } = useBradleyEnvironment()
const attachmentPolicy = computed(() => getBradleyAttachmentPolicy(newFileUploadEnabled))
const allowedTypesLabel = computed(() => bradleyDisclaimerTypesLabel(newFileUploadEnabled))

const errorList = computed(() => {
  if (Array.isArray(props.errors) && props.errors.length > 0) {
    return props.errors.filter(Boolean)
  }
  return props.error ? [props.error] : []
})

function resolveTitle(error) {
  if (!error) {
    return ''
  }

  if (error.type === 'file_count_limit') {
    return t('newBradley.uploadErrors.fileCountLimitTitle', { count: BRADLEY_MAX_FILES_PER_MESSAGE })
  }

  const fileName = error.fileName || ''
  const sizeLabel = formatBradleyFileSize(error.fileSizeBytes || 0)
  return t('newBradley.uploadErrors.attachmentRejectedTitle', {
    fileName,
    size: sizeLabel,
  })
}

function resolveDetail(error) {
  if (!error) {
    return ''
  }

  if (error.type === 'file_count_limit') {
    return t('newBradley.uploadErrors.fileCountLimitDetail', { count: BRADLEY_MAX_FILES_PER_MESSAGE })
  }

  return t('newBradley.uploadErrors.attachmentRejectedDetail', {
    sizeMb: attachmentPolicy.value.maxFileSizeMb,
    types: allowedTypesLabel.value,
  })
}

function handleDismiss(error) {
  emit('dismiss', error?.id ?? null)
}
</script>

<template>
  <div
    v-if="errorList.length > 0"
    class="bradley-upload-error-list"
  >
    <div
      v-for="(errorItem, index) in errorList"
      :key="errorItem.id || `${errorItem.fileName || 'error'}-${index}`"
      class="bradley-upload-error-banner"
      role="alert"
    >
      <div class="bradley-upload-error-banner__content">
        <span
          class="bradley-upload-error-banner__icon"
          aria-hidden="true"
        >
          <i class="pi pi-exclamation-circle" />
        </span>
        <div class="bradley-upload-error-banner__text">
          <p class="bradley-upload-error-banner__title">
            {{ resolveTitle(errorItem) }}
          </p>
          <p class="bradley-upload-error-banner__detail">
            {{ resolveDetail(errorItem) }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="bradley-upload-error-banner__dismiss"
        :aria-label="$t('newBradley.input.dismissNotification')"
        @click="handleDismiss(errorItem)"
      >
        <i class="pi pi-times" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.bradley-upload-error-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.bradley-upload-error-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  background: #fef2f2;
  color: #b91c1c;
}

.bradley-upload-error-banner__content {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  min-width: 0;
}

.bradley-upload-error-banner__icon {
  display: inline-flex;
  margin-top: 0.125rem;
  flex-shrink: 0;
  color: #dc2626;
  font-size: 1rem;
  line-height: 1;
}

.bradley-upload-error-banner__text {
  min-width: 0;
}

.bradley-upload-error-banner__title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.35;
  color: #b91c1c;
}

.bradley-upload-error-banner__detail {
  margin: 0.125rem 0 0;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.4;
  color: #dc2626;
}

.bradley-upload-error-banner__dismiss {
  display: inline-flex;
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: #b91c1c;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bradley-upload-error-banner__dismiss:hover {
  background: #fee2e2;
}

.bradley-upload-error-banner__dismiss i {
  font-size: 0.75rem;
  line-height: 1;
}
</style>
